/* External */
import {
  useMemo,
  memo,
} from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, useIntl } from 'react-intl';

/* Ott-common */
import { formatDate, isTodayOrTomorrowDay } from '@dtvgo/date-utils';
import Icon from '@dtvgo/icon';

/* Components */
import LiveProgressBar from '../LiveProgressBar';
import LiveDetail from '../LiveDetail';

/* Hooks */
import useLiveContentTexts from '../../hooks/useLiveContentTexts';
import useContent from '../../hooks/useContent';

/* Utils */
import { ImageRatio, DOT_SEPARATOR, TIME_FORMAT_FOR_SCHEDULES } from '../../utils/constants';
import {
  useFullEpisodeName,
  getRating,
  getSchedule,
  useTitle,
  isPlayingNow,
  joinArrayWithSeparator,
  joinArrayWithSpace,
} from '../../utils/metaDataUtils';
import { Channel, Schedule } from '../../storage/models';
import parentalControlBlock from '../../utils/parentalControlUtils';

/* Providers */
import { useCurrentTime } from '../../providers/CurrentTimeProvider';

/* Hooks */
import useImageCard from '../../hooks/useImageCard';

const IMG_WIDTH = 560;
const IMG_DIMENSION = ImageRatio.SIXTEEN_BY_NINE;

function EpgFocusedDetail({ schedule, isCurrent, channel }) {
  const {
    startTime,
    endTime,
    blackout,
    content: contentId,
  } = schedule || {};

  const content = useContent(contentId);
  const { title, description, images } = content || {};
  const isContentBlockedByParentalControl = parentalControlBlock(content);
  const liveContentTimeLabels = useLiveContentTexts();

  const currentTime = useCurrentTime();

  const { timeZone } = useIntl();

  const isPlayingNowFlag = useMemo(
    () => isPlayingNow(schedule, currentTime),
    [schedule, currentTime],
  );

  const { imageUrl } = useImageCard({
    images,
    imageSize: IMG_WIDTH,
    aspectRatio: IMG_DIMENSION,
  });

  const formattedSchedule = useMemo(() => getSchedule(schedule, timeZone), [schedule, timeZone]);

  const startDateFormatted = useMemo(() => {
    const day = isTodayOrTomorrowDay(startTime, liveContentTimeLabels);
    return startTime ? `${day} ${formatDate(startTime, TIME_FORMAT_FOR_SCHEDULES, timeZone)}` : '';
  }, [liveContentTimeLabels, startTime, timeZone]);

  const contentTitle = useTitle(content, schedule);
  const fullEpisodeName = useFullEpisodeName(content);

  const contentInformation = useMemo(() => joinArrayWithSpace([
    getRating(content),
    joinArrayWithSeparator([
      isPlayingNowFlag ? formattedSchedule : startDateFormatted,
      channel?.title,
    ], DOT_SEPARATOR),
  ]), [content, isPlayingNowFlag, formattedSchedule, startDateFormatted, channel?.title]);

  return (
    <div className="dtv-html5-c-epg__detail-container">
      <div
        className={`dtv-html5-c-epg__detail-preview ${
          isPlayingNowFlag ? 'dtv-html5-c-epg__detail-preview--live' : ''
        } ${
          blackout ? 'dtv-html5-c-epg__detail-preview__img-inactive' : ''
        }`}
      >
        {isContentBlockedByParentalControl && (
        <div className="dtv-html5-c-epg__detail-preview__parental-control-block">
          <Icon name="parental-block" className="dtv-html5-c-epg__detail-preview__parental-control-icon" />
        </div>
        )}
        {!blackout && isPlayingNowFlag && (
        <>
          <span className="dtv-html5-c-epg__detail-preview__schedule">
            {formattedSchedule}
          </span>
          <LiveProgressBar
            className="dtv-html5-c-epg__detail-preview__progress-bar"
            startDate={startTime}
            endDate={endTime}
          />
        </>
        )}
        <img src={imageUrl} alt={title || ''} />
      </div>
      <div className="dtv-html5-c-epg__detail-metadata">
        {schedule && (
          <LiveDetail
            startDate={startTime}
            endDate={endTime}
            schedule={schedule}
            isLiveTag={isCurrent}
          />
        )}

        <h4 className={`dtv-html5-c-epg__detail-metadata__title ${
          blackout
            ? 'dtv-html5-c-epg__detail-metadata__title-inactive'
            : ''}`}
        >
          {contentTitle || <FormattedMessage id="schedule.missing.metadata" />}
        </h4>

        {!!fullEpisodeName && (
          <p className={`dtv-html5-c-epg__detail-metadata__episode ${
            blackout
              ? 'dtv-html5-c-epg__detail-metadata__episode-inactive'
              : ''}`}
          >
            {fullEpisodeName}
          </p>
        )}
        {!!schedule && !!startDateFormatted && (
          <p className="dtv-html5-c-epg__detail-metadata__content-information">
            {contentInformation}
          </p>
        )}
        <p className="dtv-html5-c-epg__detail-metadata__description">
          {blackout ? (
            <span className="dtv-html5-c-schedule-card__blackout-text">
              <FormattedMessage
                id="common.weAreSorry"
                defaultMessage="¡Lo sentimos!"
              />
              {' '}
              <FormattedMessage
                id="epg.detail.blackout.subtitle"
                defaultMessage="Debido a derechos de transmisión, este contenido no se encuentra disponible en DIRECTV GO."
              />
            </span>
          ) : description }
        </p>
      </div>
    </div>
  );
}

EpgFocusedDetail.propTypes = {
  schedule: PropTypes.instanceOf(Schedule),
  channel: PropTypes.instanceOf(Channel),
  isCurrent: PropTypes.bool,
};

EpgFocusedDetail.defaultProps = {
  schedule: undefined,
  channel: undefined,
  isCurrent: false,
};

export default memo(EpgFocusedDetail);
